import type { VideoCallEndReason } from "@/constants/video-call";
import type { InterviewFeedbackInput } from "@/types/video-call/feedback";
import * as Sentry from "@sentry/react";
import { logCategorizedError } from "@/utils/errorCategorization";

export class CallService {
  private static getCallServiceUrl(): string {
    return import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
  }

  /**
   * End a video call
   */
  static async endCall(callId: string, organizationId: string, endedReason?: VideoCallEndReason): Promise<void> {
    // The agent marks the call log ended (with transcript and duration) when the room closes;
    // the browser only records why the call ended.
    try {
      if (endedReason) {
        await this.updateEndedReason(callId, endedReason);
      }
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('[CallService] Network error during call end:', {
          message: error.message,
          callId,
          organizationId
        });
        
        Sentry.logger.error('Network error during call end', { error: String(error),
          component: 'CallService',
          errorType: 'network',
          callId,
          organizationId
        });
        
        throw new Error('Network error: Unable to end call properly. Please check your connection.');
      }
      
      throw error;
    }
  }

  /**
   * Update the ended_reason for a call
   */
  static async updateEndedReason(callId: string, endedReason: VideoCallEndReason): Promise<void> {
    const callServiceUrl = this.getCallServiceUrl();
    
    try {
      const response = await fetch(`${callServiceUrl}/api/call/${callId}/ended-reason`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ended_reason: endedReason,
        }),
      });

      if (!response.ok) {
        const error = new Error(`Failed to update ended reason: ${response.status}`);
        
        console.error('[CallService] Failed to update ended reason:', {
          status: response.status,
          callId,
          endedReason
        });
        
        Sentry.logger.error('Failed to update ended reason', { error: String(error),
          component: 'CallService',
          httpStatus: response.status,
          callId,
          endedReason
        });
        
        throw error;
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('[CallService] Network error updating ended reason:', {
          message: error.message,
          callId,
          endedReason
        });
        
        Sentry.logger.error('Network error updating ended reason', { error: String(error),
          component: 'CallService',
          errorType: 'network',
          callId,
          endedReason
        });
      }
      
      throw error;
    }
  }

  /**
   * Immediately terminate call due to violation and stop agent
   */
  static async terminateForViolation(
    callId: string,
    organizationId: string,
    violationType: string, 
    reason: string,
    additionalData?: any
  ): Promise<void> {
    const callServiceUrl = this.getCallServiceUrl();
    
    try {
      const response = await fetch(`${callServiceUrl}/api/call/${callId}/terminate-violation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          violation_type: violationType,
          reason: reason,
          timestamp: new Date().toISOString(),
          ...additionalData
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to terminate call: ${response.status} - ${errorText}`);

        const error = new Error(`Failed to terminate call for violation: ${response.status}`);
        logCategorizedError(error, {
          component: 'CallService',
          operation: 'terminateForViolation',
          callId,
          organizationId,
          metadata: {
            httpStatus: response.status,
            errorText,
            violationType,
            reason,
            terminateUrl: `${callServiceUrl}/api/call/${callId}/terminate-violation`
          }
        });

        throw error;
      }

      await response.json();
    } catch (error) {
      console.error(`Error terminating call for violation:`, error);

      // Only log if it's not already logged (avoid double logging)
      if (!(error instanceof Error && error.message.includes('Failed to terminate call for violation'))) {
        logCategorizedError(error, {
          component: 'CallService',
          operation: 'terminateForViolation',
          callId,
          organizationId,
          metadata: {
            violationType,
            reason,
            errorType: 'unexpected_error'
          }
        });
      }

      throw error;
    }
  }

  /**
   * Submit interview feedback for a call log
   */
  static async submitInterviewFeedback(
    callId: string,
    organizationId: string,
    feedbackInput: InterviewFeedbackInput
  ): Promise<void> {
    const callServiceUrl = this.getCallServiceUrl();

    try {
      const response = await fetch(`${callServiceUrl}/api/call/${callId}/interview-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rating: feedbackInput.rating,
          feedback_text: feedbackInput.feedback_text?.trim() || undefined,
        }),
      });

      if (!response.ok) {
        let errorMessage = `Failed to submit interview feedback: ${response.status}`;
        try {
          const responseText = await response.text();
          if (responseText) {
            try {
              const parsed = JSON.parse(responseText);
              errorMessage = parsed?.message || parsed?.error || errorMessage;
            } catch {
              errorMessage = responseText;
            }
          }
        } catch (parseError) {
          console.warn("[CallService] Unable to parse feedback submission error response:", parseError);
        }

        const error = new Error(errorMessage);

        console.error("[CallService] Failed to submit interview feedback:", {
          status: response.status,
          callId,
          errorMessage,
        });

        Sentry.logger.error("Failed to submit interview feedback", { error: String(error),
          component: "CallService",
          httpStatus: response.status,
          callId,
        });

        throw error;
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        console.error("[CallService] Network error submitting interview feedback:", {
          message: error.message,
          callId,
          organizationId,
        });

        Sentry.logger.error("Network error submitting interview feedback", { error: String(error),
          component: "CallService",
          errorType: "network",
          callId,
          organizationId,
        });

        throw new Error("Network error: Unable to submit feedback. Please check your connection.");
      }

      throw error;
    }
  }
}
